/**
 * Estado de sesión del SPA: quién está dentro, y las cuatro operaciones que lo cambian.
 *
 * **La sesión no se guarda en el navegador.** La cookie es `HttpOnly`, así que el guion no
 * puede leerla ni por error, y esa es justamente la propiedad que se eligió en el diseño para
 * que un XSS no pueda extraerla. La consecuencia es que la única fuente de verdad sobre si hay
 * sesión es `GET /auth/me`, y este módulo la consulta una vez al arrancar y la conserva en
 * memoria mientras la pestaña viva. Nada de `localStorage`: un espejo de la identidad en
 * almacenamiento accesible por guion sería exactamente el dato que la cookie `HttpOnly` evita
 * exponer, y además quedaría desincronizado al cerrar sesión desde otra pestaña.
 *
 * **El sondeo inicial pide que no se reaccione al `401`.** Para quien no ha entrado, un `401` en
 * `GET /auth/me` es la respuesta correcta y no una sesión perdida: si disparara la redirección
 * del cliente de API, abrir el ingreso agregaría un destino que apunta al propio ingreso. Es la
 * única petición del SPA que se exime, y lo hace con la opción explícita del cliente.
 *
 * **`salir` limpia el estado incluso si la petición falla.** El servidor invalida la sesión
 * (criterio 1.8) y esa es la parte que importa, pero si la red se cayó justo ahí, dejar la
 * interfaz mostrando una sesión abierta sería peor que adelantarse: la cookie ya no sirve o
 * dejará de servir, y quien pidió salir tiene que ver que salió.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { LoginInput, RegisterInput } from '@aisalud/shared/schemas';
import type { ActorRole } from '@aisalud/shared/types';

import { apiClient } from './apiClient';

/** Identidad de la cuenta en sesión, tal como la devuelven las tres rutas que la informan. */
export interface CuentaEnSesion {
  readonly id: string;
  readonly email: string;
  readonly role: ActorRole;
  readonly displayName: string | null;
  readonly status: string;
}

/** Vigencia de la sesión, en instantes UTC (criterio 1.4). */
export interface VigenciaDeSesion {
  readonly issuedAt: number;
  readonly expiresAt: number;
}

/** Cuerpo de `GET /auth/me`, `POST /auth/login` y `POST /auth/register`. */
export interface RespuestaDeSesion {
  readonly account: CuentaEnSesion;
  readonly session: VigenciaDeSesion;
}

/**
 * Las tres fases, y por qué `resolviendo` es una de ellas.
 *
 * Sin fase intermedia, el primer dibujado tendría que elegir entre suponer que hay sesión o
 * suponer que no la hay, y las dos suposiciones se ven mal: la primera muestra por un instante
 * una pantalla que va a desaparecer, y la segunda manda al ingreso a quien ya estaba dentro.
 */
export type EstadoDeSesion =
  | { readonly fase: 'resolviendo' }
  | {
      readonly fase: 'conSesion';
      readonly cuenta: CuentaEnSesion;
      readonly vigencia: VigenciaDeSesion;
    }
  | { readonly fase: 'sinSesion' };

/** Lo que las vistas pueden hacer con la sesión. */
export interface Sesion {
  readonly estado: EstadoDeSesion;

  /** Inicia sesión (criterio 1.4). Lanza el error de la API para que el formulario lo muestre. */
  readonly ingresar: (credenciales: LoginInput) => Promise<CuentaEnSesion>;

  /** Crea la cuenta y deja la sesión iniciada (criterio 1.1). */
  readonly registrar: (datos: RegisterInput) => Promise<CuentaEnSesion>;

  /** Cierra la sesión en el servidor y en la interfaz (criterio 1.8). */
  readonly salir: () => Promise<void>;

  /** Deja el estado en «sin sesión» sin llamar al servidor. La usa la reacción al `401`. */
  readonly marcarPerdida: () => void;
}

const CONTEXTO = createContext<Sesion | null>(null);

/**
 * Estado de sesión de la aplicación. Lanza si se usa fuera del proveedor, en lugar de devolver
 * un valor por omisión: una vista que se cree sin sesión estando dentro de una sería un error
 * silencioso y difícil de encontrar.
 */
export function useSesion(): Sesion {
  const sesion = useContext(CONTEXTO);
  if (sesion === null) {
    throw new Error('useSesion se usó fuera de ProveedorDeSesion.');
  }
  return sesion;
}

/**
 * Resuelve la sesión al montar y la mantiene en memoria.
 *
 * @param props.children árbol que consume la sesión
 */
export function ProveedorDeSesion({ children }: { readonly children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoDeSesion>({ fase: 'resolviendo' });

  useEffect(() => {
    const control = new AbortController();

    apiClient
      .get<RespuestaDeSesion>('/auth/me', {
        senal: control.signal,
        // Ver la nota del encabezado: aquí el `401` es una respuesta y no una sesión perdida.
        sinReaccionDeSesion: true,
      })
      .then((respuesta) => {
        setEstado({
          fase: 'conSesion',
          cuenta: respuesta.account,
          vigencia: respuesta.session,
        });
      })
      .catch(() => {
        // Cualquier fallo del sondeo se lee como «no hay sesión», incluido el de red. No se
        // muestra aviso: al arrancar sin haber pedido nada, un cartel de error sería ruido, y
        // la primera acción real que la persona intente ya producirá el aviso que corresponda.
        if (control.signal.aborted) return;
        setEstado({ fase: 'sinSesion' });
      });

    return () => {
      control.abort();
    };
  }, []);

  const abrir = useCallback(
    async (camino: string, cuerpo: LoginInput | RegisterInput): Promise<CuentaEnSesion> => {
      const respuesta = await apiClient.post<RespuestaDeSesion>(camino, { cuerpo });
      setEstado({
        fase: 'conSesion',
        cuenta: respuesta.account,
        vigencia: respuesta.session,
      });
      return respuesta.account;
    },
    [],
  );

  /**
   * Se declara aparte y con identidad estable porque es la única de las cuatro que se pasa a un
   * efecto: la instalación de las reacciones del cliente de API depende de ella. Si su identidad
   * cambiara en cada dibujado del proveedor, ese efecto reinstalaría las reacciones cada vez que
   * la sesión cambia de fase, que es trabajo sin ninguna consecuencia visible.
   */
  const marcarPerdida = useCallback(() => {
    setEstado({ fase: 'sinSesion' });
  }, []);

  const salir = useCallback(async (): Promise<void> => {
    try {
      await apiClient.post<void>('/auth/logout');
    } finally {
      // Ver la nota del encabezado: la interfaz sale aunque la petición falle.
      setEstado({ fase: 'sinSesion' });
    }
  }, []);

  const sesion = useMemo<Sesion>(
    () => ({
      estado,
      ingresar: (credenciales) => abrir('/auth/login', credenciales),
      registrar: (datos) => abrir('/auth/register', datos),
      salir,
      marcarPerdida,
    }),
    [estado, abrir, salir, marcarPerdida],
  );

  return <CONTEXTO.Provider value={sesion}>{children}</CONTEXTO.Provider>;
}
